import { useEffect, useMemo, useRef } from "react";

import { playHandRaiseAudio } from "../audio";

import { t } from "i18n/i18n";
import { useAppSelector } from "store/index";
import { selectClassUsersWithIsVisibleInClassCapability } from "store/selectors";
import { selectOrderedBreakoutGroups } from "store/selectors/breakouts";
import { useListSelector } from "store/store-hooks";
import { toastInfoMessage } from "utils/alerts";
import { filterMap } from "utils/collections";

interface UseHandRaiseObserverProps {
  isInVirtualClass: boolean;
  canAuthorSection: boolean;
}

export const useHandRaiseObserver = ({
  isInVirtualClass,
  canAuthorSection,
}: UseHandRaiseObserverProps) => {
  const classUsersInVirtualClass = useListSelector(selectClassUsersWithIsVisibleInClassCapability, [
    "hand_raised_at",
  ]);
  const previousHandRaisedClassUserIds = useRef<string[] | null>(null);
  const isAudioInitialized = useRef(false);

  // Keep the latest breakout room number lookup in a ref so the observer effect
  // can read it without re-running on every breakout group change
  const orderedBreakoutGroups = useAppSelector(selectOrderedBreakoutGroups);
  const breakoutRoomNumberByGroupId = useMemo(
    () =>
      Object.fromEntries(orderedBreakoutGroups.map((group) => [group.id, group.number])) as Record<
        string,
        number
      >,
    [orderedBreakoutGroups],
  );
  const roomNumberByGroupIdRef = useRef(breakoutRoomNumberByGroupId);
  useEffect(() => {
    roomNumberByGroupIdRef.current = breakoutRoomNumberByGroupId;
  }, [breakoutRoomNumberByGroupId]);

  // Set initial values
  useEffect(() => {
    if (isAudioInitialized.current) return;

    previousHandRaisedClassUserIds.current = classUsersInVirtualClass
      ? filterMap(
          classUsersInVirtualClass,
          (classUser) => Boolean(classUser.hand_raised_at),
          (classUser) => classUser.id,
        )
      : null;

    isAudioInitialized.current = classUsersInVirtualClass.length > 0;
  }, [classUsersInVirtualClass]);

  // Hand raise: notify instructors when a participant raises their hand
  useEffect(() => {
    const shouldNotify = isInVirtualClass && canAuthorSection;
    if (!shouldNotify) return;

    const handRaisedClassUsers = classUsersInVirtualClass.filter(
      (classUser) => classUser.hand_raised_at,
    );

    const previousHandRaisedIdSet = new Set(previousHandRaisedClassUserIds.current ?? []);
    const newHandRaisedClassUsers = handRaisedClassUsers.filter(
      (classUser) => !previousHandRaisedIdSet.has(classUser.id),
    );

    for (const classUser of newHandRaisedClassUsers) {
      playHandRaiseAudio();

      // For participants in a breakout room, prompt the instructor to join and assist.
      if (classUser.breakout_group_id) {
        const roomNumber = roomNumberByGroupIdRef.current[classUser.breakout_group_id];
        if (roomNumber) {
          toastInfoMessage(t("av.breakouts.help_requested", { number: roomNumber }));
        }
      }
    }

    previousHandRaisedClassUserIds.current = handRaisedClassUsers.map((classUser) => classUser.id);
    // Take care to not enable sound unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classUsersInVirtualClass]);
};
